import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, options: {} });
  const resolver = useRef(null);

  const confirm = useCallback((options = {}) => {
    setState({ open: true, options });
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const handleResolve = (result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver.current) { resolver.current(result); resolver.current = null; }
  };

  const o = state.options;
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(open) => { if (!open) handleResolve(false); }}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-md rounded-xl" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{o.title || 'Are you sure?'}</AlertDialogTitle>
            {o.description && <AlertDialogDescription>{o.description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleResolve(false)} data-testid="confirm-cancel-button">
              {o.cancelText || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleResolve(true)}
              className={o.destructive ? 'bg-red-600 hover:bg-red-700 focus:ring-red-600' : ''}
              data-testid="confirm-ok-button"
            >
              {o.confirmText || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}
